import { gainXp } from './realms';
import { ITEMS, getRefinablePills, getRecipeInfo } from './resources.js';

// Pills/Elixirs (丹药) module

function usePill(state, name) {
  if (!name) return { success: false, messages: ['请指定丹药名称，例如 use 灵草。'] };

  const item = ITEMS[name];
  if (!item || !item.usable) {
    return { success: false, messages: [`${name} 不是可使用的物品。`] };
  }

  const effect = item.useEffect;
  if (!effect) {
    return { success: false, messages: [`${name} 没有使用效果。`] };
  }

  switch (effect.type) {
    case 'heal':
      if (state.qi >= state.maxQi) {
        return { success: false, messages: [`你的气血已满，无需使用${name}。`] };
      }
      const healed = Math.min(state.maxQi - state.qi, effect.amount);
      state.qi += healed;
      return { success: true, messages: [`服用${name}，恢复 ${healed} 气血。当前：${state.qi}/${state.maxQi}`] };

    case 'experience':
      const xpGain = effect.amount + Math.floor(Math.random() * effect.amount);
      return { success: true, messages: gainXp(state, xpGain) };

    case 'buff':
    case 'attribute':
    case 'cultivation_boost':
      return { success: true, messages: [`服用${name}，效果：${item.description}`] };

    default:
      return { success: false, messages: [`${name} 的效果类型未知。`] };
  }
}

function listPills() {
  // 返回所有可使用的丹药和草药
  return Object.entries(ITEMS)
    .filter(([_, item]) => item.usable && (item.type === 'herb' || (item.type === 'artifact' && item.useEffect)))
    .map(([name, _]) => name);
}

// Refining pills (炼丹)
function refinePill(state, pillName) {
  if (!pillName) return ['请指定丹药名称，例如 refine_pill 回气丹。'];

  const recipe = getRecipeInfo(pillName);
  if (!recipe) return [`未知丹药：${pillName}。`];

  // Check materials
  for (const ingredient of recipe.ingredients) {
    if (!(ingredient.item in state.bag) || state.bag[ingredient.item] < ingredient.count) {
      return [`材料不足：需要 ${ingredient.count} 个 ${ingredient.item}，你有 ${state.bag[ingredient.item] || 0} 个。`];
    }
  }

  // Check skill (从recipe中获取技能要求)
  const item = ITEMS[pillName];
  if (!item.recipe || !state.skills[item.recipe.skill]) {
    return [`需要掌握 ${item.recipe.skill} 才能炼制。`];
  }

  // Consume materials
  for (const ingredient of recipe.ingredients) {
    state.bag[ingredient.item] -= ingredient.count;
    if (state.bag[ingredient.item] <= 0) delete state.bag[ingredient.item];
  }

  // Refine
  const success = Math.random() < recipe.successRate;
  if (success) {
    addItemToBag(state, pillName);
    return [`炼丹成功！获得 ${pillName} 一枚。`];
  } else {
    return [`炼丹失败，材料尽失。`];
  }
}

// Helper to add item to bag (since it's defined in cmd-world.js, but we can replicate)
function addItemToBag(state, itemName, count = 1) {
  if (state.bag[itemName]) {
    state.bag[itemName] += count;
  } else {
    state.bag[itemName] = count;
  }
}

function listRefinablePills() {
  return getRefinablePills();
}

function getPillRecipe(pillName) {
  return getRecipeInfo(pillName);
}

function getAllPillRecipes() {
  const pills = {};
  getRefinablePills().forEach(pillName => {
    pills[pillName] = getRecipeInfo(pillName);
  });
  return pills;
}

export { usePill, listPills, refinePill, listRefinablePills, getPillRecipe, getAllPillRecipes };
